import type {
  AccessSnapshot, AdminCharacterBinding, AdminCharacterCreateInput, AdminCharacterCreator, AdminCharacterDeleteResult, AdminCharacterImage, AdminCharacterProfile, AdminCharacterSdPrompts, AdminCharacterVoice, AdminContextPolicySnapshot, AdminRole, AdminSearchTerm, AdminSearchTermInput, AdminSystemPrompt, AdminUsageSnapshot, CharacterImageKind, GuildMemberPage,
  ImageModelCatalogItem, ImageModelCatalogPage, ImagePromptPreset, ImagePromptPresetPage, ImageUsageSnapshot, InteractionBlockDetail, InteractionBlockPage, ManualImagePublicJob,
  CheckedVoiceSource, SettingsMutation, SettingsSnapshot, SynthesisSettings, UserCharacterImagePrompts, UserCharacterSummary, UserCharacterVoice, VoiceSourcePage, VoiceStudioJob,
} from "./types";
import {
  parseAccessSnapshot, parseAdminCharacter, parseAdminCharacterCreator, parseAdminCharacterDeleteResult, parseAdminCharacterImage, parseAdminCharacterProfile, parseAdminCharacterSdPrompts, parseAdminCharacterVoice, parseAdminCharacters, parseAdminContextPolicySnapshot, parseAdminRoles,
  parseAdminUsageSnapshot, parseGuildMemberPage, parseInteractionBlockDetail,
  parseCheckedVoiceSource, parseImagePromptPreset, parseImagePromptPresetPage, parseImageUsageSnapshot, parseInteractionBlockPage, parseManualImageHistoryClear, parseManualImageJob, parseSettingsSnapshot, parseUserCharacterImagePrompts, parseUserCharacters, parseUserCharacterVoice, parseVoiceSourcePage, parseVoiceStudioJob, parseVoiceStudioJobs,
} from "./settings-protocol";
import { imageJobQueueUrl, parseImageJobQueuePage, type ImageJobQueuePage } from "./image-job-queue";

const SETTINGS_URL = "/v1/discord-settings/me";
const SESSION_EXCHANGE_URL = "/v1/discord-settings/session/exchange";
// Activity iframe 只能可靠地从自己的 Pages origin 加载模块。该文件由仓库固定版本和哈希，
// 不会把 Discord client secret 打进前端。
const DISCORD_EMBEDDED_SDK_URL = "/vendor/discord-embedded-app-sdk-2.5.0.mjs";
const IMAGE_MODELS_URL = "/v1/civitai/models";
const IMAGE_MODELS_QUERY_MAX_LENGTH = 80;
const IMAGE_MODELS_CURSOR_MAX_LENGTH = 512;
const IMAGE_MODELS_CURSOR_PATTERN = /^[A-Za-z0-9._~|-]+$/;
const IMAGE_MODELS_TAG_MAX_LENGTH = 80;
const IMAGE_MODELS_TAG_PATTERN = /^[^\u0000-\u001f\u007f]+$/u;
const discordClientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
const discordRedirectUri = import.meta.env.VITE_DISCORD_REDIRECT_URI;

interface DiscordSdk {
  ready(): Promise<void>;
  commands: {
    authorize(input: { client_id: string; response_type: "code"; state: string; prompt: "none"; scope: string[] }): Promise<{ code: string }>;
  };
}

interface DiscordSdkModule {
  DiscordSDK: new (clientId: string) => DiscordSdk;
}

async function createDiscordSdk(): Promise<DiscordSdk> {
  // Vite 保留该动态 URL，由 Discord Activity 运行时拉取官方 SDK；不把 bot/client secret 打包进前端。
  const sdkModule = await import(/* @vite-ignore */ DISCORD_EMBEDDED_SDK_URL) as DiscordSdkModule;
  return new sdkModule.DiscordSDK(requiredEnv(discordClientId, "VITE_DISCORD_CLIENT_ID"));
}

function requiredEnv(value: string | undefined, name: "VITE_DISCORD_CLIENT_ID" | "VITE_DISCORD_REDIRECT_URI"): string {
  if (!value) throw new Error(`${name}_missing`);
  return value;
}

async function getDiscordAuthorizationCode(): Promise<string> {
  const sdk = await createDiscordSdk();
  await sdk.ready();
  const authorization = await sdk.commands.authorize({
    client_id: requiredEnv(discordClientId, "VITE_DISCORD_CLIENT_ID"),
    response_type: "code",
    state: crypto.randomUUID(),
    prompt: "none",
    scope: ["identify"]
  });
  // OAuth code 只能交给服务端校验/兑换；前端从不持有 Discord client secret。
  // Settings API 将它作为一次性身份凭据兑换为当前请求的服务端会话。
  return authorization.code;
}

interface SettingsSessionClientOptions {
  fetchImpl: typeof fetch;
  getAuthorizationCode: () => Promise<string>;
  redirectUri: () => string;
}

export class SettingsRequestError extends Error {
  constructor(readonly status: number, readonly code?: string) {
    super(`settings_request_failed_${status}`);
  }
}

export function settingsSaveErrorMessage(error: unknown): string {
  if (error instanceof SettingsRequestError) {
    if (error.code === "title_name_taken" || error.code === "discord_activity_title_name_taken") return "这个头衔名称已被使用，请换一个。";
    if (error.code === "discord_title_special_read_only") return "这是受保护的特殊头衔，只能切换显示，不能修改或删除。";
    if (error.code === "discord_title_multiple_custom_roles") return "当前持有多个自定义头衔，请管理员先核对角色配置。";
    if (error.code === "discord_title_role_shared") return "旧头衔还有其他成员使用，不能删除该权限组，请管理员处理。";
    if (error.code === "discord_title_role_member_count_missing") return "暂时无法确认旧头衔的成员数量，任务进度已保留，请稍后重试。";
    if (error.code === "role_custom_member_conflict") return "无法开启：该角色的部分成员已有其他自定义头衔。";
    if (error.code === "role_custom_policy_changed") return "头衔配置刚刚被更新，请刷新列表后重试。";
    if (error.code === "discord_role_member_enumeration_unavailable") return "无法完整核对成员头衔，未修改配置，请稍后重试。";
    if (error.code === "discord_activity_enabled_title_empty") return "启用头衔后，请先填写你喜欢的头衔。";
    if (error.code === "discord_activity_revision_conflict") return "设置已发生变化。草稿已保留，请重新打开面板核对后再保存。";
    if (error.code === "avatar_consent_required") return "这个化身的绑定已变化，请重新打开角色卡并确认隐私提示。";
  }
  return "保存失败，请稍后重试。";
}

export function createSettingsSessionClient(options: SettingsSessionClientOptions) {
  let sessionUsable = false;
  let sessionGeneration = 0;
  let exchangeInFlight: Promise<void> | undefined;

  async function exchangeSession(): Promise<void> {
    if (!exchangeInFlight) {
      exchangeInFlight = (async () => {
        const code = await options.getAuthorizationCode();
        const response = await options.fetchImpl(SESSION_EXCHANGE_URL, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, redirect_uri: options.redirectUri() })
        });
        if (!response.ok) throw new Error(`settings_session_exchange_failed_${response.status}`);
        sessionUsable = true;
        sessionGeneration += 1;
      })();
    }
    const currentExchange = exchangeInFlight;
    try {
      await currentExchange;
    } finally {
      // Promise 只表示正在进行的 exchange，成功后不能永久充当 sessionReady。
      if (exchangeInFlight === currentExchange) exchangeInFlight = undefined;
    }
  }

  async function ensureSession(): Promise<void> {
    if (!sessionUsable) await exchangeSession();
  }

  async function refreshUnauthorizedSession(failedGeneration: number): Promise<void> {
    // 同一代会话的并发 401 只共享一个 exchange；后来者若已观察到新一代则直接复用。
    if (sessionUsable && sessionGeneration !== failedGeneration) return;
    sessionUsable = false;
    await exchangeSession();
  }

  async function fetchAuthenticated(url: string, init?: RequestInit): Promise<Response> {
    const headers = new Headers(init?.headers);
    const isFormDataBody = typeof FormData !== "undefined" && init?.body instanceof FormData;
    if (init?.body !== undefined && !isFormDataBody && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    return options.fetchImpl(url, {
      ...init,
      headers,
      credentials: "include"
    });
  }

  async function requestResponseAt(url: string, init?: RequestInit): Promise<Response> {
      await ensureSession();
      const requestGeneration = sessionGeneration;
      let response = await fetchAuthenticated(url, init);
      if (response.status === 401) {
        await refreshUnauthorizedSession(requestGeneration);
        const retryGeneration = sessionGeneration;
        // 复用原 init/body；PUT 的 request_id 与第一次尝试完全相同。
        response = await fetchAuthenticated(url, init);
        if (response.status === 401 && sessionGeneration === retryGeneration) {
          sessionUsable = false;
        }
      }
      return response;
  }

  async function requestAt<T>(url: string, init?: RequestInit): Promise<T> {
      const response = await requestResponseAt(url, init);
      if (!response.ok) {
        let code: string | undefined;
        try {
          const value = await response.json() as { error?: unknown };
          if (typeof value.error === "string" && /^[a-z0-9_-]{1,100}$/.test(value.error)) code = value.error;
        } catch { /* 不展示上游原始错误体。 */ }
        throw new SettingsRequestError(response.status, code);
      }
      if (response.status === 204) return undefined as T;
      return (await response.json()) as T;
  }

  return {
    request: <T>(init?: RequestInit) => requestAt<T>(SETTINGS_URL, init),
    requestAt,
    requestBlobAt: async (url: string): Promise<Blob> => {
      const response = await requestResponseAt(url, { headers: { Accept: "audio/*" }, cache: "no-store" });
      if (!response.ok) throw new SettingsRequestError(response.status);
      return response.blob();
    },
  };
}

const settingsSessionClient = createSettingsSessionClient({
  fetchImpl: (...args) => fetch(...args),
  getAuthorizationCode: getDiscordAuthorizationCode,
  redirectUri: () => requiredEnv(discordRedirectUri, "VITE_DISCORD_REDIRECT_URI")
});

function isImageModelCatalogItem(value: unknown): value is ImageModelCatalogItem {
  if (!value || typeof value !== "object") return false;
  const item = value as Record<string, unknown>;
  return typeof item.ref === "string"
    && typeof item.name === "string"
    && typeof item.imageUrl === "string"
    && typeof item.modelId === "number"
    && typeof item.versionId === "number"
    && Array.isArray(item.versions)
    && item.versions.every((version) => {
      if (!version || typeof version !== "object") return false;
      const candidate = version as Record<string, unknown>;
      return typeof candidate.name === "string"
        && typeof candidate.id === "number"
        && typeof candidate.ref === "string"
        && typeof candidate.imageUrl === "string";
    });
}

function catalogCursor(cursor?: string): string | undefined {
  if (typeof cursor !== "string" || cursor.length === 0 || cursor.length > IMAGE_MODELS_CURSOR_MAX_LENGTH) return undefined;
  return IMAGE_MODELS_CURSOR_PATTERN.test(cursor) ? cursor : undefined;
}

function catalogTag(tag?: string): string | undefined {
  if (tag === undefined) return undefined;
  const normalized = tag.trim();
  if (normalized.length === 0) return undefined;
  if (normalized.length > IMAGE_MODELS_TAG_MAX_LENGTH || !IMAGE_MODELS_TAG_PATTERN.test(normalized)) {
    throw new Error("image_models_tag_invalid");
  }
  return normalized;
}

function imageModelsUrl(query?: string, cursor?: string, tag?: string): string {
  const normalized = query?.trim().slice(0, IMAGE_MODELS_QUERY_MAX_LENGTH);
  const params = new URLSearchParams();
  if (normalized) params.set("q", normalized);
  const normalizedTag = catalogTag(tag);
  if (normalizedTag) params.set("tag", normalizedTag);
  const normalizedCursor = catalogCursor(cursor);
  if (normalizedCursor) params.set("cursor", normalizedCursor);
  const encoded = params.toString();
  return encoded ? `${IMAGE_MODELS_URL}?${encoded}` : IMAGE_MODELS_URL;
}

async function loadImageModels(query?: string, cursor?: string, tag?: string): Promise<ImageModelCatalogPage> {
  // 模型目录是公开同源数据，不需要 Discord Activity 会话，也不应触发 OAuth code 兑换。
  const response = await fetch(imageModelsUrl(query, cursor, tag), { credentials: "omit" });
  if (!response.ok) throw new Error(`image_models_request_failed_${response.status}`);

  const payload: unknown = await response.json();
  if (!payload || typeof payload !== "object" || !Array.isArray((payload as { items?: unknown }).items)) {
    throw new Error("image_models_response_invalid");
  }
  const { items, nextCursor } = payload as { items: unknown[]; nextCursor?: unknown };
  if (!items.every(isImageModelCatalogItem)) throw new Error("image_models_response_invalid");
  if (nextCursor !== undefined && (typeof nextCursor !== "string" || catalogCursor(nextCursor) === undefined)) {
    throw new Error("image_models_response_invalid");
  }
  const validatedNextCursor = typeof nextCursor === "string" ? catalogCursor(nextCursor) : undefined;
  return validatedNextCursor ? { items, nextCursor: validatedNextCursor } : { items };
}

interface AuthenticatedSessionClient {
  request<T>(init?: RequestInit): Promise<T>;
  requestAt<T>(url: string, init?: RequestInit): Promise<T>;
  requestBlobAt(url: string): Promise<Blob>;
}

function jsonMutation(method: "POST" | "PUT" | "PATCH" | "DELETE", value: unknown): RequestInit {
  return { method, body: JSON.stringify(value) };
}

function adminGuildMembersUrl(query: string, cursor?: string): string {
  const params = new URLSearchParams();
  const normalizedQuery = query.trim().slice(0, 80);
  if (normalizedQuery) params.set("query", normalizedQuery);
  if (cursor && cursor.length <= 512) params.set("after", cursor);
  const encoded = params.toString();
  return encoded ? `/v1/discord-settings/admin/guild-members?${encoded}` : "/v1/discord-settings/admin/guild-members";
}

function interactionsUrl(cursor?: string, characterId?: string): string {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor.slice(0, 512));
  if (characterId) {
    if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(characterId)) throw new Error("interaction_character_filter_invalid");
    params.set("character_id", characterId);
  }
  if (!params.size) return "/v1/discord-settings/interactions";
  return `/v1/discord-settings/interactions?${params.toString()}`;
}

function imagePresetUrl(cursor?: string): string {
  const params = new URLSearchParams({ limit: "100" });
  if (cursor !== undefined) {
    if (!/^[A-Za-z0-9._~|:+=/-]{1,512}$/.test(cursor)) throw new Error("image_preset_cursor_invalid");
    params.set("cursor", cursor);
  }
  return `/v1/discord-settings/image/presets?${params.toString()}`;
}

const VOICE_PAGE_SIZE = 20;
const VOICE_TAG_MAX_LENGTH = 100;
const VOICE_TAG_PATTERN = /^[^\u0000-\u001f\u007f]+$/u;

function voiceSourceTags(tags: readonly string[]): string[] {
  const normalized = tags.map((tag) => tag.trim()).filter(Boolean);
  if (normalized.some((tag) => tag.length > VOICE_TAG_MAX_LENGTH || !VOICE_TAG_PATTERN.test(tag))) {
    throw new Error("voice_sources_tag_invalid");
  }
  return [...new Set(normalized)];
}

function voiceSourcesUrl(query = "", cursor?: string, tags: readonly string[] = []): { url: string; pageNumber: number } {
  const pageNumber = cursor && /^\d+$/.test(cursor) ? Number(cursor) : 1;
  const params = new URLSearchParams({ page_size: String(VOICE_PAGE_SIZE), page_number: String(pageNumber) });
  const normalizedQuery = query.trim().slice(0, 100);
  if (normalizedQuery) params.set("title", normalizedQuery);
  for (const tag of voiceSourceTags(tags)) params.append("tag", tag);
  return { url: `/v1/discord-settings/voices?${params.toString()}`, pageNumber };
}

function adminPrompt(value: unknown): AdminSystemPrompt {
  if (!value || typeof value !== "object") throw new Error("admin_system_prompt_response_invalid");
  const item = value as Record<string, unknown>;
  if (typeof item.key !== "string" || !item.key || typeof item.label !== "string" || !item.label || typeof item.value !== "string" || typeof item.editable !== "boolean" || !["router", "service", "helper", "roleplay", "summary", "image", "style"].includes(String(item.category)) || !["system", "user", "assistant"].includes(String(item.value_kind)) || (item.immutable_reason !== undefined && typeof item.immutable_reason !== "string") || !Number.isSafeInteger(item.revision) || (item.revision as number) < 0) throw new Error("admin_system_prompt_response_invalid");
  return item as unknown as AdminSystemPrompt;
}

function adminPromptList(value: unknown): AdminSystemPrompt[] {
  const items = value && typeof value === "object" ? (value as Record<string, unknown>).prompts : undefined;
  if (!Array.isArray(items)) throw new Error("admin_system_prompts_response_invalid");
  return items.map(adminPrompt);
}

function adminSearchTerm(value: unknown): AdminSearchTerm {
  if (!value || typeof value !== "object") throw new Error("admin_search_term_response_invalid");
  const item = value as Record<string, unknown>;
  if (typeof item.id !== "string" || !item.id || typeof item.name !== "string" || !item.name || !Array.isArray(item.aliases) || item.aliases.some((alias) => typeof alias !== "string") || typeof item.description !== "string" || !Number.isSafeInteger(item.revision) || (item.revision as number) < 0) throw new Error("admin_search_term_response_invalid");
  return { id: item.id, name: item.name, aliases: item.aliases as string[], description: item.description, revision: item.revision as number };
}

function adminSearchTermList(value: unknown): AdminSearchTerm[] {
  const items = Array.isArray(value) ? value : value && typeof value === "object" ? (value as Record<string, unknown>).terms : undefined;
  if (!Array.isArray(items)) throw new Error("admin_search_terms_response_invalid");
  return items.map(adminSearchTerm);
}

export function createSettingsApi(session: AuthenticatedSessionClient) {
  return {
    load: async (): Promise<SettingsSnapshot> => parseSettingsSnapshot(await session.request<unknown>()),
    save: async (changes: SettingsMutation): Promise<SettingsSnapshot> => parseSettingsSnapshot(await session.request<unknown>(jsonMutation("PUT", changes))),
    loadAccess: async (): Promise<AccessSnapshot> => parseAccessSnapshot(await session.requestAt<unknown>("/v1/discord-settings/access")),
    deleteTitle: async (requestId: string): Promise<SettingsSnapshot> => parseSettingsSnapshot(await session.requestAt<unknown>("/v1/discord-settings/title/delete", jsonMutation("POST", { request_id: requestId }))),
    loadAdminUsage: async (): Promise<AdminUsageSnapshot> => parseAdminUsageSnapshot(await session.requestAt<unknown>("/v1/discord-settings/admin/usage")),
    loadAdminContextPolicy: async (): Promise<AdminContextPolicySnapshot> => parseAdminContextPolicySnapshot(await session.requestAt<unknown>("/v1/discord-settings/admin/context-policy")),
    updateAdminContextPolicy: async (input: {
      context_max_messages: number;
      expected_revision: number;
      request_id: string;
    }): Promise<AdminContextPolicySnapshot> => parseAdminContextPolicySnapshot(await session.requestAt<unknown>(
      "/v1/discord-settings/admin/context-policy",
      jsonMutation("PUT", input),
    )),
    updateAdminUsageSettings: async (input: {
      engine: AdminUsageSnapshot["tts_daily"]["engine"];
      image_global_daily_limit: number;
      image_user_daily_limit: number | null;
      audio_global_daily_limit_ms: number | null;
      audio_user_daily_limit_ms: number | null;
      expected_tts_revision: number;
      request_id: string;
    }): Promise<AdminUsageSnapshot> => parseAdminUsageSnapshot(await session.requestAt<unknown>("/v1/discord-settings/admin/usage-settings", jsonMutation("PUT", input))),
    updateAdminImageLimit: async (limit: number, requestId: string): Promise<void> => { await session.requestAt("/v1/discord-settings/admin/image-limit", jsonMutation("PUT", { limit, request_id: requestId })); },
    resetAdminImageUsage: async (requestId: string): Promise<void> => { await session.requestAt("/v1/discord-settings/admin/image-reset", jsonMutation("POST", { request_id: requestId })); },
    loadAdminRoles: async (): Promise<AdminRole[]> => parseAdminRoles(await session.requestAt<unknown>("/v1/discord-settings/admin/roles")),
    updateAdminRoleCustom: async (roleId: string, custom: boolean, requestId: string): Promise<void> => { await session.requestAt(`/v1/discord-settings/admin/roles/${encodeURIComponent(roleId)}`, jsonMutation("PUT", { custom, request_id: requestId })); },
    loadAdminSystemPrompts: async (): Promise<AdminSystemPrompt[]> => adminPromptList(await session.requestAt<unknown>("/v1/discord-settings/admin/prompts")),
    updateAdminSystemPrompt: async (key: string, value: string, expectedRevision: number, requestId = crypto.randomUUID()): Promise<Pick<AdminSystemPrompt, "key" | "value" | "revision">> => {
      const response = await session.requestAt<unknown>(`/v1/discord-settings/admin/prompts/${encodeURIComponent(key)}`, jsonMutation("PUT", { value, expected_revision: expectedRevision, request_id: requestId }));
      if (!response || typeof response !== "object") throw new Error("admin_system_prompt_response_invalid");
      const item = response as Record<string, unknown>;
      if (item.key !== key || typeof item.value !== "string" || !Number.isSafeInteger(item.revision) || (item.revision as number) < 1) throw new Error("admin_system_prompt_response_invalid");
      return { key, value: item.value, revision: item.revision as number };
    },
    loadAdminSearchTerms: async (): Promise<AdminSearchTerm[]> => adminSearchTermList(await session.requestAt<unknown>("/v1/discord-settings/terms")),
    createAdminSearchTerm: async (input: AdminSearchTermInput, requestId = crypto.randomUUID()): Promise<AdminSearchTerm> => adminSearchTerm(await session.requestAt<unknown>(
      "/v1/discord-settings/terms",
      jsonMutation("POST", { ...input, request_id: requestId }),
    )),
    updateAdminSearchTerm: async (id: string, input: AdminSearchTermInput, expectedRevision: number, requestId = crypto.randomUUID()): Promise<AdminSearchTerm> => adminSearchTerm(await session.requestAt<unknown>(
      `/v1/discord-settings/terms/${encodeURIComponent(id)}`,
      jsonMutation("PUT", { ...input, expected_revision: expectedRevision, request_id: requestId }),
    )),
    deleteAdminSearchTerm: async (id: string, expectedRevision: number): Promise<void> => {
      await session.requestAt(`/v1/discord-settings/terms/${encodeURIComponent(id)}`, jsonMutation("DELETE", { expected_revision: expectedRevision }));
    },
    loadAdminCharacters: async (): Promise<AdminCharacterBinding[]> => parseAdminCharacters(await session.requestAt<unknown>("/v1/discord-settings/admin/characters")),
    loadUserCharacters: async (): Promise<UserCharacterSummary[]> => parseUserCharacters(await session.requestAt<unknown>("/v1/discord-settings/characters")),
    loadVoiceSources: async (query = "", cursor?: string, tags: readonly string[] = []): Promise<VoiceSourcePage> => {
      const page = voiceSourcesUrl(query, cursor, tags);
      return parseVoiceSourcePage(await session.requestAt<unknown>(page.url), page.pageNumber, VOICE_PAGE_SIZE);
    },
    checkVoiceSource: async (voiceSourceId: string): Promise<CheckedVoiceSource> => parseCheckedVoiceSource(await session.requestAt<unknown>(
      `/v1/discord-settings/voices/${encodeURIComponent(voiceSourceId)}/check`,
    )),
    loadVoiceStudioJobs: async (): Promise<VoiceStudioJob[]> => parseVoiceStudioJobs(await session.requestAt<unknown>(
      "/v1/discord-settings/voice-studio/jobs",
    )),
    createVoiceStudioJob: async (input: { request_id: string; text: string; voice_id: string; synthesis_settings: SynthesisSettings }): Promise<VoiceStudioJob> => parseVoiceStudioJob(await session.requestAt<unknown>(
      "/v1/discord-settings/voice-studio/jobs",
      jsonMutation("POST", input),
    )),
    updateVoiceStudioJobTitle: async (jobId: string, title: string): Promise<VoiceStudioJob> => parseVoiceStudioJob(await session.requestAt<unknown>(
      `/v1/discord-settings/voice-studio/jobs/${encodeURIComponent(jobId)}`,
      jsonMutation("PATCH", { title }),
    )),
    deleteVoiceStudioJob: async (jobId: string): Promise<void> => {
      await session.requestAt(`/v1/discord-settings/voice-studio/jobs/${encodeURIComponent(jobId)}`, { method: "DELETE" });
    },
    loadVoiceStudioAudio: async (jobId: string): Promise<Blob> => session.requestBlobAt(
      `/v1/discord-settings/voice-studio/jobs/${encodeURIComponent(jobId)}/audio`,
    ),
    loadUserCharacterVoice: async (characterId: string): Promise<UserCharacterVoice> => parseUserCharacterVoice(await session.requestAt<unknown>(
      `/v1/discord-settings/characters/${encodeURIComponent(characterId)}/voice`,
    )),
    updateUserCharacterVoice: async (characterId: string, voiceSourceId: string | null, synthesisSettings: SynthesisSettings | null | undefined, expectedRevision: number, requestId: string): Promise<UserCharacterVoice> => parseUserCharacterVoice(await session.requestAt<unknown>(
      `/v1/discord-settings/characters/${encodeURIComponent(characterId)}/voice`,
      jsonMutation("PUT", { voice: voiceSourceId ?? "", ...(synthesisSettings === undefined ? {} : { synthesis_settings: synthesisSettings }), expected_revision: expectedRevision, request_id: requestId }),
    )),
    loadSharedCharacterVoice: async (characterId: string): Promise<AdminCharacterVoice> => parseAdminCharacterVoice(await session.requestAt<unknown>(`/v1/discord-settings/characters/${encodeURIComponent(characterId)}/shared-voice`)),
    updateSharedCharacterVoice: async (characterId: string, voice: string, synthesisSettings: SynthesisSettings | null | undefined, expectedVoiceRevision: number): Promise<AdminCharacterVoice> => parseAdminCharacterVoice(await session.requestAt<unknown>(
      `/v1/discord-settings/characters/${encodeURIComponent(characterId)}/shared-voice`,
      jsonMutation("PUT", { voice, ...(synthesisSettings === undefined ? {} : { synthesis_settings: synthesisSettings }), expected_voice_revision: expectedVoiceRevision }),
    )),
    createAdminCharacter: async (input: AdminCharacterCreateInput): Promise<AdminCharacterBinding> => {
      const form = new FormData();
      form.set("metadata", JSON.stringify({
        request_id: input.request_id,
        name: input.name,
        age: input.age,
        height_cm: input.height_cm,
        profile_text: input.profile_text,
        positive_prompt: input.positive_prompt,
        owner_uid: input.owner_uid,
      }));
      form.set("portrait", input.portrait);
      form.set("cover", input.cover);
      return parseAdminCharacter(await session.requestAt<unknown>("/v1/discord-settings/admin/characters", { method: "POST", body: form }));
    },
    createUserCharacter: async (input: AdminCharacterCreateInput): Promise<AdminCharacterBinding> => {
      const form = new FormData();
      form.set("metadata", JSON.stringify({
        request_id: input.request_id,
        name: input.name,
        age: input.age,
        height_cm: input.height_cm,
        profile_text: input.profile_text,
        positive_prompt: input.positive_prompt,
        owner_uid: input.owner_uid,
      }));
      form.set("portrait", input.portrait);
      form.set("cover", input.cover);
      return parseAdminCharacter(await session.requestAt<unknown>("/v1/discord-settings/characters", { method: "POST", body: form }));
    },
    deleteAdminCharacter: async (characterId: string, requestId: string, expectedBindingVersion: number): Promise<AdminCharacterDeleteResult> => parseAdminCharacterDeleteResult(await session.requestAt<unknown>(
      `/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}`,
      jsonMutation("DELETE", { request_id: requestId, expected_binding_version: expectedBindingVersion }),
    )),
    updateAdminCharacterBinding: async (characterId: string, ownerUid: string | null, expectedBindingVersion: number): Promise<AdminCharacterBinding> => parseAdminCharacter(await session.requestAt<unknown>(
      `/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}/binding`,
      jsonMutation("PUT", { owner_uid: ownerUid, expected_binding_version: expectedBindingVersion }),
    )),
    updateAdminCharacterCreator: async (characterId: string, creatorUid: string | null, expectedCreatorRevision: number, requestId: string): Promise<AdminCharacterCreator> => {
      return parseAdminCharacterCreator(await session.requestAt<unknown>(
        `/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}/creator`,
        jsonMutation("PUT", { creator_uid: creatorUid, expected_creator_revision: expectedCreatorRevision, request_id: requestId }),
      ));
    },
    deleteUserCharacter: async (characterId: string, requestId: string, expectedBindingVersion: number): Promise<AdminCharacterDeleteResult> => parseAdminCharacterDeleteResult(await session.requestAt<unknown>(
      `/v1/discord-settings/characters/${encodeURIComponent(characterId)}`,
      jsonMutation("DELETE", { request_id: requestId, expected_binding_version: expectedBindingVersion }),
    )),
    updateUserCharacterBinding: async (characterId: string, ownerUid: string | null, expectedBindingVersion: number): Promise<AdminCharacterBinding> => parseAdminCharacter(await session.requestAt<unknown>(
      `/v1/discord-settings/characters/${encodeURIComponent(characterId)}/binding`,
      jsonMutation("PUT", { owner_uid: ownerUid, expected_binding_version: expectedBindingVersion }),
    )),
    loadUserCharacterProfile: async (characterId: string): Promise<AdminCharacterProfile> => parseAdminCharacterProfile(await session.requestAt<unknown>(`/v1/discord-settings/characters/${encodeURIComponent(characterId)}/profile`)),
    updateUserCharacterProfile: async (characterId: string, content: string, expectedProfileRevision: number): Promise<AdminCharacterProfile> => parseAdminCharacterProfile(await session.requestAt<unknown>(
      `/v1/discord-settings/characters/${encodeURIComponent(characterId)}/profile`,
      jsonMutation("PUT", { content, expected_profile_revision: expectedProfileRevision }),
    )),
    loadUserCharacterSdPrompts: async (characterId: string): Promise<AdminCharacterSdPrompts> => parseAdminCharacterSdPrompts(await session.requestAt<unknown>(`/v1/discord-settings/characters/${encodeURIComponent(characterId)}/sd-prompts`)),
    updateUserCharacterSdPrompts: async (characterId: string, positivePrompt: string, negativePrompt: string, expectedSdRevision: number): Promise<AdminCharacterSdPrompts> => parseAdminCharacterSdPrompts(await session.requestAt<unknown>(
      `/v1/discord-settings/characters/${encodeURIComponent(characterId)}/sd-prompts`,
      jsonMutation("PUT", { positive_prompt: positivePrompt, negative_prompt: negativePrompt, expected_sd_revision: expectedSdRevision }),
    )),
    loadUserCharacterImagePrompts: async (characterId: string): Promise<UserCharacterImagePrompts> => parseUserCharacterImagePrompts(await session.requestAt<unknown>(`/v1/discord-settings/characters/${encodeURIComponent(characterId)}/image-prompts`)),
    updateUserCharacterImagePrompts: async (characterId: string, positivePrompt: string | null, negativePrompt: string | null, expectedRevision: number, requestId: string): Promise<UserCharacterImagePrompts> => parseUserCharacterImagePrompts(await session.requestAt<unknown>(
      `/v1/discord-settings/characters/${encodeURIComponent(characterId)}/image-prompts`,
      jsonMutation("PUT", { positive_prompt: positivePrompt, negative_prompt: negativePrompt, expected_revision: expectedRevision, request_id: requestId }),
    )),
    loadAdminCharacterProfile: async (characterId: string): Promise<AdminCharacterProfile> => parseAdminCharacterProfile(await session.requestAt<unknown>(`/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}/profile`)),
    updateAdminCharacterProfile: async (characterId: string, content: string, expectedProfileRevision: number): Promise<AdminCharacterProfile> => parseAdminCharacterProfile(await session.requestAt<unknown>(
      `/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}/profile`,
      jsonMutation("PUT", { content, expected_profile_revision: expectedProfileRevision }),
    )),
    loadAdminCharacterSdPrompts: async (characterId: string): Promise<AdminCharacterSdPrompts> => parseAdminCharacterSdPrompts(await session.requestAt<unknown>(`/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}/sd-prompts`)),
    updateAdminCharacterSdPrompts: async (characterId: string, positivePrompt: string, negativePrompt: string, expectedSdRevision: number): Promise<AdminCharacterSdPrompts> => parseAdminCharacterSdPrompts(await session.requestAt<unknown>(
      `/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}/sd-prompts`,
      jsonMutation("PUT", { positive_prompt: positivePrompt, negative_prompt: negativePrompt, expected_sd_revision: expectedSdRevision }),
    )),
    loadAdminCharacterVoice: async (characterId: string): Promise<AdminCharacterVoice> => parseAdminCharacterVoice(await session.requestAt<unknown>(`/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}/voice`)),
    updateAdminCharacterVoice: async (characterId: string, voice: string, expectedVoiceRevision: number): Promise<AdminCharacterVoice> => parseAdminCharacterVoice(await session.requestAt<unknown>(
      `/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}/voice`,
      jsonMutation("PUT", { voice, expected_voice_revision: expectedVoiceRevision }),
    )),
    loadAdminCharacterImage: async (characterId: string, kind: CharacterImageKind): Promise<AdminCharacterImage> => parseAdminCharacterImage(await session.requestAt<unknown>(
      `/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}/images/${kind}`,
    )),
    updateAdminCharacterImage: async (characterId: string, kind: CharacterImageKind, file: File, expectedRevision: number): Promise<AdminCharacterImage> => parseAdminCharacterImage(await session.requestAt<unknown>(
      `/v1/discord-settings/admin/characters/${encodeURIComponent(characterId)}/images/${kind}`,
      { method: "PUT", body: file, headers: { "Content-Type": file.type, "X-Image-Revision": String(expectedRevision) } },
    )),
    loadUserCharacterImage: async (characterId: string, kind: CharacterImageKind): Promise<AdminCharacterImage> => parseAdminCharacterImage(await session.requestAt<unknown>(
      `/v1/discord-settings/characters/${encodeURIComponent(characterId)}/images/${kind}`,
    )),
    updateUserCharacterImage: async (characterId: string, kind: CharacterImageKind, file: File, expectedRevision: number): Promise<AdminCharacterImage> => parseAdminCharacterImage(await session.requestAt<unknown>(
      `/v1/discord-settings/characters/${encodeURIComponent(characterId)}/images/${kind}`,
      { method: "PUT", body: file, headers: { "Content-Type": file.type, "X-Image-Revision": String(expectedRevision) } },
    )),
    loadGuildMembers: async (query = "", cursor?: string): Promise<GuildMemberPage> => parseGuildMemberPage(await session.requestAt<unknown>(adminGuildMembersUrl(query, cursor))),
    consentAvatar: async (characterId: string, bindingVersion: number): Promise<void> => {
      const result = await session.requestAt<unknown>("/v1/discord-settings/avatars/consent", jsonMutation("POST", { character_id: characterId, binding_version: bindingVersion }));
      if (!result || typeof result !== "object" || (result as { ok?: unknown }).ok !== true) throw new Error("avatar_consent_response_invalid");
    },
    consentCharacterInteraction: async (characterId: string, authorityKind: "creator" | "avatar", authorityRevision: number): Promise<void> => {
      const result = await session.requestAt<unknown>("/v1/discord-settings/interactions/consent", jsonMutation("POST", { character_id: characterId, authority_kind: authorityKind, authority_revision: authorityRevision }));
      if (!result || typeof result !== "object" || (result as { ok?: unknown }).ok !== true) throw new Error("interaction_consent_response_invalid");
    },
    loadInteractions: async (cursor?: string, characterId?: string): Promise<InteractionBlockPage> => parseInteractionBlockPage(await session.requestAt<unknown>(interactionsUrl(cursor, characterId))),
    loadInteraction: async (blockId: string): Promise<InteractionBlockDetail> => parseInteractionBlockDetail(await session.requestAt<unknown>(`/v1/discord-settings/interactions/${encodeURIComponent(blockId)}`)),
    createManualImageJob: async (requestId: string, positivePrompt: string, negativePrompt: string): Promise<ManualImagePublicJob> => parseManualImageJob(await session.requestAt<unknown>(
      "/v1/discord-settings/image/jobs",
      jsonMutation("POST", { request_id: requestId, positive_prompt: positivePrompt, negative_prompt: negativePrompt }),
    )),
    loadImagePromptPresets: async (cursor?: string): Promise<ImagePromptPresetPage> => parseImagePromptPresetPage(await session.requestAt<unknown>(
      imagePresetUrl(cursor),
    )),
    createImagePromptPreset: async (requestId: string, title: string | undefined, positivePrompt: string, negativePrompt: string): Promise<ImagePromptPreset> => parseImagePromptPreset(await session.requestAt<unknown>(
      "/v1/discord-settings/image/presets",
      jsonMutation("POST", { request_id: requestId, ...(title === undefined ? {} : { title }), positive_prompt: positivePrompt, negative_prompt: negativePrompt }),
    )),
    renameImagePromptPreset: async (presetId: string, requestId: string, title: string, expectedRevision: number): Promise<ImagePromptPreset> => parseImagePromptPreset(await session.requestAt<unknown>(
      `/v1/discord-settings/image/presets/${encodeURIComponent(presetId)}`,
      jsonMutation("PUT", { request_id: requestId, title, expected_revision: expectedRevision }),
    )),
    deleteImagePromptPreset: async (presetId: string, requestId: string, expectedRevision: number): Promise<void> => {
      await session.requestAt(`/v1/discord-settings/image/presets/${encodeURIComponent(presetId)}`, jsonMutation("DELETE", { request_id: requestId, expected_revision: expectedRevision }));
    },
    loadImageUsage: async (): Promise<ImageUsageSnapshot> => parseImageUsageSnapshot(await session.requestAt<unknown>(
      "/v1/discord-settings/image/usage",
    )),
    clearManualImageHistory: async (requestId: string): Promise<{ cleared: number }> => parseManualImageHistoryClear(await session.requestAt<unknown>(
      "/v1/discord-settings/image/jobs/clear",
      jsonMutation("POST", { request_id: requestId }),
    )),
    cancelManualImageJob: async (jobId: string): Promise<ManualImagePublicJob> => parseManualImageJob(await session.requestAt<unknown>(
      `/v1/discord-settings/image/jobs/${encodeURIComponent(jobId)}/cancel`,
      jsonMutation("POST", {}),
    )),
    deleteManualImageJob: async (jobId: string): Promise<void> => {
      await session.requestAt(`/v1/discord-settings/image/jobs/${encodeURIComponent(jobId)}`, { method: "DELETE" });
    },
    loadManualImageJob: async (jobId: string, signal?: AbortSignal): Promise<ManualImagePublicJob> => parseManualImageJob(await session.requestAt<unknown>(
      `/v1/discord-settings/image/jobs/${encodeURIComponent(jobId)}`,
      signal ? { signal } : undefined,
    )),
    loadImageJobQueue: async (cursor?: string, signal?: AbortSignal): Promise<ImageJobQueuePage> => parseImageJobQueuePage(await session.requestAt<unknown>(
      imageJobQueueUrl(cursor),
      signal ? { signal } : undefined,
    )),
    loadImageModels,
  };
}

export const settingsApi = createSettingsApi(settingsSessionClient);
